import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';
import { Edit, Clock, User, ChevronRight, ArrowLeft, ArrowRight, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { KBArticle, useArticle, usePlaybookChildren, useArticles } from '@/hooks/useKnowledgeBase';
import { useUserRole } from '@/hooks/useUserRole';
import { useAppUsers } from '@/hooks/useAppUsers';
import { ReactNode } from 'react';

// Remove primeiro H1 se for igual ao título do artigo (evita duplicação)
function removeFirstH1IfMatchesTitle(content: string, title: string): string {
  const firstH1Match = content.match(/^#\s+(.+?)(\r?\n|$)/);
  
  if (firstH1Match) {
    const h1Text = firstH1Match[1].trim();
    if (h1Text.toLowerCase() === title.toLowerCase() || 
        title.toLowerCase().includes(h1Text.toLowerCase()) ||
        h1Text.toLowerCase().includes(title.toLowerCase())) {
      return content.replace(/^#\s+.+?(\r?\n|$)/, '').trimStart();
    }
  }
  
  return content;
}

interface KBArticleViewProps {
  article: KBArticle;
}

const CATEGORY_LABELS: Record<string, string> = {
  playbook: 'Playbook',
  guide: 'Guia',
  reference: 'Referência',
};

const CATEGORY_COLORS: Record<string, string> = {
  playbook: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  guide: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  reference: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

// Helper to extract text content from React children
function getTextContent(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return getTextContent((children as { props: { children: ReactNode } }).props.children);
  }
  return '';
}

// Custom markdown components for professional rendering
const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold mt-10 mb-6 pb-3 border-b-2 border-border text-foreground first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold mt-8 mb-4 text-foreground">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold mt-6 mb-3 text-foreground">
      {children}
    </h3>
  ),
  p: ({ children }) => {
    const textContent = getTextContent(children);
    if (textContent.startsWith('→') || textContent.startsWith('➡')) {
      return (
        <div className="flex items-center gap-2 my-3 px-4 py-2 bg-muted/50 border-l-3 border-primary rounded-r text-primary font-medium">
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
          <span>{textContent.replace(/^[→➡]\s*/, '')}</span>
        </div>
      );
    }
    return <p className="my-4 leading-relaxed">{children}</p>;
  },
  blockquote: ({ children }) => (
    <blockquote className="my-6 px-6 py-4 bg-gradient-to-r from-primary/10 to-primary/5 border-l-4 border-primary rounded-r-lg not-italic">
      {children}
    </blockquote>
  ),
  ul: ({ children }) => (
    <ul className="my-4 space-y-2 pl-6 list-disc marker:text-primary">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 space-y-2 pl-6 list-decimal marker:text-primary marker:font-semibold">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed pl-1">
      {children}
    </li>
  ),
  table: ({ children }) => (
    <div className="my-6 overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-muted">
      {children}
    </thead>
  ),
  th: ({ children }) => (
    <th className="px-4 py-3 text-left font-semibold text-foreground border-b border-border">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 border-b border-border">
      {children}
    </td>
  ),
  tr: ({ children }) => (
    <tr className="even:bg-muted/50">
      {children}
    </tr>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className={className}>
          {children}
        </code>
      );
    }
    return (
      <code className="px-1.5 py-0.5 bg-muted text-primary rounded text-sm font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-6 p-4 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto">
      {children}
    </pre>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">
      {children}
    </strong>
  ),
  a: ({ href, children }) => (
    <a 
      href={href} 
      className="text-primary underline underline-offset-2 hover:opacity-80"
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  ),
  hr: () => (
    <hr className="my-8 border-t border-border" />
  ),
};

export function KBArticleView({ article }: KBArticleViewProps) {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const { users = [] } = useAppUsers();
  const { data: allArticles = [] } = useArticles();
  const isAdmin = role === 'admin';

  // Get parent article if this is a child
  const parentArticle = useMemo(() => {
    if (!article.parent_id) return null;
    return allArticles.find(a => a.id === article.parent_id);
  }, [article.parent_id, allArticles]);

  // Get siblings if this article has a parent
  const { data: siblings = [] } = usePlaybookChildren(article.parent_id || undefined);

  // Get children if this is a playbook
  const { data: children = [] } = usePlaybookChildren(article.category === 'playbook' && !article.parent_id ? article.id : undefined);

  const updatedByUser = users.find((u) => u.id === article.updated_by);
  const updatedByName = updatedByUser?.display_name || updatedByUser?.first_name || 'Sistema';

  // Calculate previous/next navigation for articles within a playbook
  const navigation = useMemo(() => {
    if (!article.parent_id || siblings.length === 0) return null;
    
    const currentIndex = siblings.findIndex((s) => s.id === article.id);
    if (currentIndex === -1) return null;
    
    return {
      prev: currentIndex > 0 ? siblings[currentIndex - 1] : null,
      next: currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null,
      current: currentIndex + 1,
      total: siblings.length,
    };
  }, [article.id, article.parent_id, siblings]);

  const isPlaybook = article.category === 'playbook' && !article.parent_id;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Breadcrumb for child articles */}
      {parentArticle && (
        <div className="px-8 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <button 
              onClick={() => navigate(`/knowledge-base/${parentArticle.slug}`)}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              📚 {parentArticle.title}
            </button>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground font-medium">{article.title}</span>
            {navigation && (
              <span className="ml-auto text-xs">
                {navigation.current} de {navigation.total}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-border px-8 py-6 flex items-start justify-between gap-4 bg-background">
        <div className="space-y-3">
          {/* Category badge */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span 
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[article.category] || 'bg-muted text-muted-foreground'}`}
            >
              {CATEGORY_LABELS[article.category] || article.category}
            </span>
            {isPlaybook && children.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {children.length} artigos
              </Badge>
            )}
            {!article.is_published && (
              <Badge variant="destructive" className="text-xs ml-2">
                Rascunho
              </Badge>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-foreground tracking-tight">
            {article.title}
          </h1>
          
          {/* Meta info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              Atualizado em{' '}
              {safeFormat(new Date(article.updated_at), "d 'de' MMMM 'de' yyyy", {
                locale: ptBR,
              })}
            </span>
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {updatedByName}
            </span>
          </div>
        </div>

        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/knowledge-base/edit/${article.slug}`)}
            className="flex-shrink-0"
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        )}
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="px-8 py-8 max-w-4xl">
          {/* Playbook Index - show table of contents for playbooks */}
          {isPlaybook && children.length > 0 && (
            <div className="mb-10 p-6 bg-muted/50 rounded-lg border border-border">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <List className="h-5 w-5" />
                Índice do Playbook
              </h2>
              <ol className="space-y-2">
                {children.map((child, index) => (
                  <li key={child.id}>
                    <button
                      onClick={() => navigate(`/knowledge-base/${child.slug}`)}
                      className="flex items-center gap-3 w-full text-left p-2 rounded hover:bg-muted transition-colors group"
                    >
                      <span className="w-6 h-6 flex items-center justify-center bg-primary/10 text-primary text-sm font-medium rounded">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-foreground group-hover:text-primary transition-colors">
                        {child.title}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <article className="kb-article">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {removeFirstH1IfMatchesTitle(article.content, article.title)}
            </ReactMarkdown>
          </article>

          {/* Navigation for articles within a playbook */}
          {navigation && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex items-center justify-between gap-4">
                {navigation.prev ? (
                  <button
                    onClick={() => navigate(`/knowledge-base/${navigation.prev!.slug}`)}
                    className="flex items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors group flex-1 text-left"
                  >
                    <ArrowLeft className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    <div>
                      <div className="text-xs text-muted-foreground">Anterior</div>
                      <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {navigation.prev.title}
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="flex-1" />
                )}
                
                {navigation.next ? (
                  <button
                    onClick={() => navigate(`/knowledge-base/${navigation.next!.slug}`)}
                    className="flex items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors group flex-1 text-right justify-end"
                  >
                    <div>
                      <div className="text-xs text-muted-foreground">Próximo</div>
                      <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {navigation.next.title}
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
